import React, { useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import ReactECharts from 'echarts-for-react';
import {
  Table,
  Row,
  Col,
} from 'antd';
import {
  ExportOutlined,
} from '@ant-design/icons';
import {
  TableCell,
  TableTitle,
} from './Table';
import {
  formatLPValue,
} from '../utils/Chart';
import {
  Node,
  TreeItem,
  ProjectNode,
  useGetTree,
  useListMeter,
} from '../services/projectApi';
import { exportDataAsCSV } from '../utils/exportData';
import { useAuth, useEvent } from '../Providers';
import { formatDurtionStr } from '../utils/converter';
import { WidgetProps } from './types';

interface OfflineCategory {
  name: string;
  key: string;
  lowerBoundary: number
  upperBoundary: number
}

export const OfflineByCategory: React.FC<WidgetProps> = (props) => {
  const auth = useAuth();
  const [nowRef, setNowRef] = useState<Moment>(moment());
  const [cateList, setCateList] = useState([
    {
      name: "Warning (4 - 8 Hrs)",
      key: "w",
      lowerBoundary: 4,
      upperBoundary: 8,
    },
    {
      name: "Critical (8 - 48 Hrs)",
      lowerBoundary: 8,
      key: "c",
      upperBoundary: 24,
    },
    {
      name: "Severe (48 Hrs Up)",
      lowerBoundary: 48,
      key: "s",
      upperBoundary: Infinity,
    },
  ].reverse());
  const [offlineDict, setOfflineDict] = useState<Record<string, Array<Node>>>({});
  const [locationDict, setLocationDict] = useState<Record<number, string>>({});
  const [longestOfflineList, setLongestOfflineList] = useState<Array<Node>>([]);
  const [columns, setColumns] = useState<any>([]);

  const param: object = {
    accountId: auth.account?.accountid,
    onlineStatus: 0,
  };
  const {
    data: meterNodes,
    isFetching: isNodeFetching,
    isSuccess: isNodeLoadSuccess,
    error: nodeError,
    refetch,
  } = useListMeter(param);

  const {
    data: treeNodes,
    isFetching: isTreeFetching,
    isSuccess: isTreeSuccess,
    error: treeError,
  } = useGetTree();

  function staticColumn(cate: OfflineCategory, locationDict: Record<number, string>) {
    return [{
      title: <TableTitle>{`${cate.name}`}</TableTitle>,
      children: [
        {
          title: () => <TableTitle>Meter No.</TableTitle>,
          render: (node: Node, _: any, index: number) => <TableCell>{node.disc}</TableCell>,
          width: 100,
        },
        {
          title: () => <TableTitle>Province</TableTitle>,
          render: (node: Node, _: any, index: number) => <TableCell>{node.parentId ? locationDict[node.parentId]: '-'}</TableCell>,
          sorter: (a: Node, b: Node) =>  a.parentId && b.parentId ? locationDict[a.parentId].localeCompare(locationDict[b.parentId]) : 0,
          ellipsis: true,
          width: 70,
        },
        {
          title: () => <TableTitle>Duration</TableTitle>,
          render: (node: Node, _: any, index: number) => {
            const onlineTime = node.onlineTime;
            if (!onlineTime) {
              return <TableCell></TableCell>;
            }
            const diff = moment().diff(moment(onlineTime));
            return <TableCell>{formatDurtionStr(diff, false) ?? ""}</TableCell>;
          },
          sorter: (a: Node, b: Node) =>  a.onlineTime && b.onlineTime ? new Date(a.onlineTime).valueOf() - new Date(b.onlineTime).valueOf() : 0,
          align: 'right' as const,
          ellipsis: true,
          width: 70,
        }
      ]
    }];
  }

  function paths(node: TreeItem<ProjectNode>, path: Array<string>, dict: Record<string, Array<string>>) {
    if (node.children === undefined || node.children.length === 0) {
      dict[node.value.objid] = [...path, node.value.disc];
      return
    }

    node.children.map(c => paths(c, [...path, node.value.disc], dict));
  }

  useEffect(() => {
    if (props.reloadCount > 0) {
        refetch();
    }
}, [props.reloadCount]);

  useEffect(() => {
    if (!isTreeFetching && isTreeSuccess && !!!treeError && treeNodes) {
      // console.log(treeNodes);
      let locationDict: Record<string, Array<string>> = {};
      paths(treeNodes[0], [], locationDict);

      const newDict = Object.keys(locationDict).reduce((dict, key) => {
        return {
          ...dict,
          [parseInt(key)]: locationDict[key].at(-1),
        }
      }, {});
      setLocationDict(newDict);
    }
  }, [isTreeFetching, isTreeSuccess, treeError, treeNodes])

  useEffect(() => {
    if (!isNodeFetching && isNodeLoadSuccess && !!!nodeError && meterNodes) {

      const dict = cateList.reduce<Record<string, Array<Node>>>((dict, cate) => {
        const nodesByCate = meterNodes.filter(node => {
          const diff = nowRef.diff(moment(node.onlineTime), 'hour');
          return diff >= cate.lowerBoundary && diff < cate.upperBoundary;
        })
        .sort((a, b) => {
          if (a.onlineTime == undefined || b.onlineTime === undefined) {
            return 0;
          }
          return a.onlineTime.localeCompare(b.onlineTime);
        });

        return {
          ...dict,
          [cate.key]: nodesByCate,
        };
      }, {});

      setOfflineDict(dict);

      const longest = Object.values(dict).sort((a, b) => a.length - b.length).pop();
      if (longest) {
        setLongestOfflineList(longest);
      }
    }
  }, [isNodeFetching, isNodeLoadSuccess,  nodeError, meterNodes]);

  // function exportLoadProfile(asGroup: boolean) {
  //   const content = tableData.map(row => row.map(item => (item instanceof Date) ? `"${item.toLocaleString('en-GB')}"` : (item === null ? '-' : String(item))));
  //   const header = asGroup
  //     ? data.flatMap(item => [`${item.name} Timestamp`, `${item.name}(${lpType})`])
  //     : ['Timestamp', ...data.map(item => `${item.name}(${lpType})`)];
  //   exportDataAsCSV([header, ...content], `loadprofile_${props.meterNo ? props.meterNo : 'nodes'}.csv`);
  // }

  return (
      <Row style={{ width: '100%', height: '100%'}} justify="space-between">
        {cateList.map((cate) => {
          const meters = offlineDict[cate.key];
          return <Col key={`col-oc-${cate.key}`}>
            <Table
              style={{ alignSelf: 'flex-start', width: '100%', height: '100%'}}
              loading={isNodeFetching}
              className="chart"
              dataSource={meters}
              columns={staticColumn(cate, locationDict)}
              scroll={{ x: true }}
              footer={() => <TableCell>{`Total: ${meters?.length} meters`}</TableCell>}
              pagination={{ position: ['bottomCenter'], showSizeChanger: false, pageSize: 8 }} />
          </Col>
        })}
      </Row>
  );
};
